const SESSION_STORAGE_KEY = "context.workspaceSession";
/** The pre-token key, kept only so a stale value can be cleared on upgrade. */
const LEGACY_ID_KEY = "context.workspaceId";

export interface WorkspaceSession {
  workspaceId: string;
  token: string;
}

/**
 * The browser's proof that it owns its writing space.
 *
 * The workspace id used to be generated client-side and sent as a plain header,
 * which meant anyone who learned an id could act as that student. The id is now
 * minted by the service together with a signed token, and only the token is
 * presented — so possession, not knowledge of an identifier, is what grants
 * access. This is the sole thing the app keeps in browser storage.
 */
export function readStoredSession(storage: Storage | undefined = safeStorage()): WorkspaceSession | null {
  try {
    const raw = storage?.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<WorkspaceSession>;
    if (typeof parsed?.workspaceId !== "string" || typeof parsed?.token !== "string") return null;
    return { workspaceId: parsed.workspaceId, token: parsed.token };
  } catch {
    return null;
  }
}

export function storeSession(
  session: WorkspaceSession,
  storage: Storage | undefined = safeStorage(),
): void {
  try {
    storage?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    // A leftover bare id from the previous scheme is no longer a credential.
    storage?.removeItem(LEGACY_ID_KEY);
  } catch {
    // Private-mode browsers can refuse writes; the session still works in-memory.
  }
}

export function clearSession(storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Nothing to do: the caller is already recovering from a rejected token.
  }
}

function safeStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

export { SESSION_STORAGE_KEY, LEGACY_ID_KEY };
